import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { currentFiscalYear, formatFY, formatStartDate } from "@/lib/fiscal";
import { cn } from "@/lib/utils";
import { CopyToFYSelect, SAVED_BORDER, SectionHeader, useSaveFlash } from "./shared";

interface CampusAffiliateRow {
  id: string;
  campus: string;
  fiscalYear: number;
  startDate: string | null;
  year: number | null;
}

function formatStartDateLabel(dateStr: string): string {
  return `Started ${formatStartDate(dateStr)}`;
}

export function CampusAffiliatesSection({
  ownerId,
  ownerName,
  fiscalYear,
  onDataChange,
  onRemove,
  readOnly = false,
}: {
  ownerId: string;
  ownerName: string;
  fiscalYear: number;
  onDataChange: (hasData: boolean) => void;
  onRemove?: () => void;
  readOnly?: boolean;
}) {
  const currentFY = currentFiscalYear();
  const navigate = useNavigate();
  const [campuses, setCampuses] = useState<CampusAffiliateRow[]>([]);
  const [allCampuses, setAllCampuses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { savedField: savedFlashId, flashSaved } = useSaveFlash();
  const [comboboxOpen, setComboboxOpen] = useState(false);

  async function createCampusAndNavigate(campusName: string) {
    await fetch("/api/campuses", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: campusName }),
    });
    navigate({
      to: `/campuses?fromOwner=${ownerId}&ownerName=${encodeURIComponent(ownerName)}&tab=campus-affiliates`,
    });
  }

  const fetchAssigned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/owners/${ownerId}/campus-affiliates?fy=${fiscalYear}`);
      if (res.ok) {
        const data = await res.json();
        setCampuses(data.campuses);
        onDataChange(data.campuses.length > 0);
      }
    } finally {
      setLoading(false);
    }
  }, [ownerId, fiscalYear, onDataChange]);

  const fetchAllCampuses = useCallback(async () => {
    const res = await fetch("/api/deal-values/campuses");
    if (res.ok) {
      const data = await res.json();
      setAllCampuses(data.values ?? []);
    }
  }, []);

  useEffect(() => {
    fetchAssigned();
  }, [fetchAssigned]);

  useEffect(() => {
    fetchAllCampuses();
  }, [fetchAllCampuses]);

  const assignedSet = new Set(campuses.map((c) => c.campus));
  const availableToAdd = allCampuses.filter((c) => !assignedSet.has(c));

  async function syncCampuses(updatedList: string[], changedCampus: string) {
    const res = await fetch(`/api/owners/${ownerId}/campus-affiliates`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, campuses: updatedList }),
    });
    if (res.ok) {
      const data = await res.json();
      setCampuses(data.campuses);
      onDataChange(data.campuses.length > 0);
      flashSaved(changedCampus);
    }
  }

  function addCampus(campus: string) {
    const updatedList = [...campuses.map((c) => c.campus), campus];
    syncCampuses(updatedList, campus);
  }

  function removeCampus(campus: string) {
    const updatedList = campuses.filter((c) => c.campus !== campus).map((c) => c.campus);
    syncCampuses(updatedList, campus);
  }

  async function removeAll() {
    await fetch(`/api/owners/${ownerId}/campus-affiliates`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fiscalYear, campuses: [] }),
    });
    setCampuses([]);
    onDataChange(false);
    onRemove?.();
  }

  async function copyToFY(targetFY: number) {
    if (campuses.length === 0) return;
    await fetch(`/api/owners/${ownerId}/campus-affiliates`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        fiscalYear: targetFY,
        campuses: campuses.map((c) => c.campus),
      }),
    });
  }

  return (
    <Card>
      <CardContent className="pt-5">
        <SectionHeader title="Campus Affiliates" onRemove={readOnly ? undefined : removeAll}>
          {!readOnly && campuses.length > 0 && (
            <CopyToFYSelect currentFY={currentFY} selectedFY={fiscalYear} onCopy={copyToFY} />
          )}
        </SectionHeader>

        <div className={loading ? "opacity-60" : ""}>
          {campuses.length === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-2">
              No campus affiliates for {formatFY(fiscalYear)}.{!readOnly && " Add one below."}
            </p>
          ) : (
            <div className="space-y-2">
              {campuses.map((c) => (
                <div
                  key={c.id}
                  className={cn(
                    "flex items-center justify-between rounded-md border px-3 py-2 transition-[border-color,box-shadow] duration-300",
                    savedFlashId === c.campus && SAVED_BORDER,
                    c.startDate === null && "border-destructive",
                  )}
                >
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{c.campus}</span>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {c.year !== null ? `Year ${c.year}` : "Year ?"}
                      </Badge>
                    </div>
                    {c.startDate ? (
                      <span className="text-xs text-muted-foreground">
                        {formatStartDateLabel(c.startDate)}
                      </span>
                    ) : (
                      <span className="text-xs text-destructive flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        No start date —{" "}
                        <button
                          type="button"
                          className="underline hover:text-destructive/80"
                          onClick={() => createCampusAndNavigate(c.campus)}
                        >
                          configure
                        </button>
                      </span>
                    )}
                  </div>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeCampus(c.campus)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!readOnly && availableToAdd.length > 0 && (
            <div className="mt-4">
              <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[250px] h-9 justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <Plus className="h-3.5 w-3.5" />
                      Add Campus
                    </span>
                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search campuses..." />
                    <CommandList>
                      <CommandEmpty>No campus found.</CommandEmpty>
                      {availableToAdd.map((campus) => (
                        <CommandItem
                          key={campus}
                          value={campus}
                          onSelect={() => {
                            addCampus(campus);
                            setComboboxOpen(false);
                          }}
                        >
                          {campus}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
